import { useEffect, useState, useRef } from "react";
import { Stethoscope, Clock, RotateCcw, Loader2, Send, ImagePlus, X, FileText } from "lucide-react";
import { reviewsForQuestion } from "../lib/reviews";
import { getDoctorRequestStatus, replyDoctorRequest, uploadPhoto, fileUrl } from "../lib/api";
import { toast } from "sonner";

const attachmentsOf = (m) => {
  if (Array.isArray(m.attachments) && m.attachments.length) return m.attachments;
  if (m.attachment_path) return [{ path: m.attachment_path, name: m.attachment_name }];
  return [];
};

const Thread = ({ thread }) =>
  Array.isArray(thread) && thread.length > 0 ? (
    <div className="space-y-2 mb-3">
      {thread.map((m, j) => {
        const atts = attachmentsOf(m);
        return (
          <div
            key={j}
            className={`rounded-xl px-4 py-2.5 text-base leading-relaxed whitespace-pre-line ${
              m.role === "physician"
                ? "bg-white border-2 border-emerald-200 text-slate-800"
                : "bg-slate-50 border-2 border-slate-200 text-slate-700"
            }`}
          >
            <span className="block text-xs font-bold uppercase tracking-wide mb-0.5 text-slate-500">
              {m.role === "physician" ? "Physician" : "You"}
            </span>
            {m.text}
            {atts.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {atts.map((a, k) =>
                  /\.pdf$/i.test(a.path || a.name || "") ? (
                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700">
                      <FileText className="w-4 h-4 text-red-500" /> {a.name || "PDF document"}
                    </a>
                  ) : (
                    <a key={k} href={fileUrl(a.path, a.sig)} target="_blank" rel="noreferrer">
                      <img
                        src={fileUrl(a.path, a.sig)}
                        alt={a.name || "attachment"}
                        className="max-h-48 rounded-lg border border-slate-200"
                      />
                    </a>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  ) : null;

const ReviewItem = ({ item, index }) => {
  const [status, setStatus] = useState(item.status);
  const [thread, setThread] = useState(item.thread || []);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replied, setReplied] = useState(false);
  const [photos, setPhotos] = useState([]);
  const fileRef = useRef(null);

  const pickPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...photos];
    for (const f of files) {
      if (next.length >= 3) {
        toast.error("You can attach up to 3 photos.");
        break;
      }
      if (!/^image\//.test(f.type)) {
        toast.error("Please choose image files only.");
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 10 MB).`);
        continue;
      }
      next.push(f);
    }
    setPhotos(next);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const sendReply = async () => {
    const msg = reply.trim();
    if ((!msg && photos.length === 0) || sending) return;
    setSending(true);
    try {
      let attachments = [];
      if (photos.length) attachments = await Promise.all(photos.map((f) => uploadPhoto(f)));
      await replyDoctorRequest(item.request_id, msg, attachments);
      const entry = { role: "visitor", text: msg };
      if (attachments.length) entry.attachments = attachments;
      setThread((t) => [...t, entry]);
      setStatus("reopened");
      setReplied(true);
      setReply("");
      setPhotos([]);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Reply sent — a physician will follow up.");
    } catch (e) {
      toast.error("Couldn't send your reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (item.refunded) {
    return (
      <div data-testid={`review-history-item-${index}`} className="flex items-center gap-2 text-base text-slate-600">
        <RotateCcw className="w-5 h-5 text-slate-500 shrink-0" /> This review was refunded.
      </div>
    );
  }

  if (status === "reviewed") {
    return (
      <div data-testid={`review-history-item-${index}`} className="bg-white border-2 border-emerald-200 rounded-xl p-4">
        <p className="text-sm font-bold text-emerald-700 mb-2">A physician reviewed your question:</p>
        {thread.length > 0 ? (
          <Thread thread={thread} />
        ) : item.review_note ? (
          <p className="text-lg text-slate-800 leading-relaxed whitespace-pre-line mb-3" data-testid={`review-history-note-${index}`}>
            {item.review_note}
          </p>
        ) : (
          <p className="text-base text-slate-600 mb-3">Your review is complete. Any notes were sent to your email.</p>
        )}
        <div className="pt-2 border-t border-slate-100">
          <label className="block text-sm font-semibold text-slate-600 mb-1.5">Have a follow-up? Reply to the physician:</label>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={2}
            placeholder="Type your follow-up question…"
            data-testid={`review-reply-input-${index}`}
            className="w-full bg-white border-2 border-emerald-200 focus:border-emerald-400 rounded-lg px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 outline-none resize-y"
          />
          {photos.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid={`review-photo-preview-${index}`}>
              {photos.map((p, k) => (
                <div key={`${p.name}-${p.size}-${p.lastModified}`} className="relative">
                  <img src={URL.createObjectURL(p)} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                  <button
                    onClick={() => removePhoto(k)}
                    data-testid={`review-photo-remove-${index}-${k}`}
                    className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 text-slate-500 hover:text-slate-800 shadow-sm"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickPhoto} data-testid={`review-photo-input-${index}`} className="hidden" />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={sendReply}
              disabled={sending || (!reply.trim() && photos.length === 0)}
              data-testid={`review-reply-button-${index}`}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-base px-5 py-2.5 rounded-xl transition-transform active:scale-95"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Send reply
            </button>
            <button
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={photos.length >= 3}
              data-testid={`review-photo-add-${index}`}
              className="inline-flex items-center gap-2 border-2 border-emerald-200 hover:border-emerald-400 disabled:opacity-50 text-emerald-700 font-semibold text-base px-4 py-2.5 rounded-xl transition-colors"
            >
              <ImagePlus className="w-5 h-5" /> Add photo{photos.length ? ` (${photos.length}/3)` : ""}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`review-history-item-${index}`}>
      <Thread thread={thread} />
      <div className="flex items-center gap-2 text-base text-slate-600" data-testid={`review-history-pending-${index}`}>
        <Clock className="w-5 h-5 text-emerald-600 shrink-0" />
        {replied
          ? "Your reply was sent — a physician will follow up. We'll email you when it's ready."
          : `A physician review is ${item.paid ? "in progress" : "pending"}. We'll email you when it's ready.`}
      </div>
    </div>
  );
};

export const ReviewHistory = ({ question }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    const saved = reviewsForQuestion(question);
    if (saved.length === 0) {
      setItems([]);
      return;
    }
    Promise.all(
      saved.map((r) => getDoctorRequestStatus(r.request_id).catch(() => null))
    ).then((results) => setItems(results.filter(Boolean)));
  }, [question]);

  if (items === null) {
    return (
      <div className="flex items-center gap-2 text-base text-slate-500" data-no-print>
        <Loader2 className="w-4 h-4 animate-spin" /> Checking your physician reviews…
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-5 sm:p-6" data-testid="review-history" data-no-print>
      <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-3">
        <Stethoscope className="w-6 h-6 text-emerald-600" /> Your Physician Review
      </h3>
      <div className="space-y-3">
        {items.map((it, i) => (
          <ReviewItem key={it.request_id || i} item={it} index={i} />
        ))}
      </div>
    </div>
  );
};
